import { forwardRef } from 'react';
import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

interface ViewIconButtonProps extends Omit<ButtonProps, 'children'> {
  tooltip?: string;
  showTooltip?: boolean;
}

/**
 * Standardized view icon button with primary color on hover.
 */
export const ViewIconButton = forwardRef<HTMLButtonElement, ViewIconButtonProps>(
  ({ tooltip, showTooltip = true, className, ...props }, ref) => {
    const { t } = useTranslation('common');
    const label = tooltip || t('app.view');

    const button = (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        aria-label={label}
        className={cn('hover:bg-primary/10 hover:text-primary', className)}
        {...props}
      >
        <Eye className="h-4 w-4" />
      </Button>
    );

    if (!showTooltip) return button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }
);

ViewIconButton.displayName = 'ViewIconButton';
